import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, LogOut, Loader2, Download, Search, BookOpen, Users, Plus, Pencil, Trash2, Save, X, Wallet, ImagePlus, User, Images, Sparkles, Copy, Mail, FileDown, TrendingDown } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { PRODUCTS } from "../lib/payments";
import { LogoMark } from "../components/Logo";
import { CLIENTS } from "../components/Transformations";
import TransformationsManager from "../components/admin/TransformationsManager";

const STATUSES = ["New", "Contacted", "Onboarded"];
const CATEGORIES = ["Nutrition", "Fat Loss", "Muscle Gain", "Dietetics", "Lifestyle", "Recipes"];

const EMPTY_ARTICLE = { title: "", category: "Nutrition", excerpt: "", content: "", published: true };

const inputCls =
  "w-full rounded-xl bg-ink border border-line px-4 py-3 text-white placeholder:text-slate-600 focus:border-accent focus:outline-none transition-colors";

const rupees = (paise) =>
  (paise / 100).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });

function toCSV(leads) {
  const headers = ["Date", "Full Name", "Contact", "Age & Location", "Current Weight", "Obstacle", "Target Goal", "Status"];
  const rows = leads.map((l) => [
    l.created_at,
    l.full_name,
    l.contact,
    l.age_location,
    l.current_weight,
    l.obstacle,
    l.target_goal,
    l.status,
  ]);
  return [headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
    .join("\n");
}

export default function Admin() {
  const [checking, setChecking] = useState(true);
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState("leads");

  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");

  const [articles, setArticles] = useState([]);
  const [editor, setEditor] = useState(null);
  const [savingArticle, setSavingArticle] = useState(false);

  const [payments, setPayments] = useState([]);

  const [photos, setPhotos] = useState({});
  const [uploading, setUploading] = useState(null);
  const fileInputs = useRef({});

  const [gallery, setGallery] = useState([]);
  const [galleryChunk, setGalleryChunk] = useState(24);
  const [galleryBusy, setGalleryBusy] = useState(null);

  const [planFor, setPlanFor] = useState(null);
  const [planText, setPlanText] = useState("");
  const [planLoading, setPlanLoading] = useState(false);

  const loadLeads = async () => {
    try {
      setLeads(await api("/leads"));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const loadArticles = async () => {
    try {
      setArticles(await api("/articles/all"));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const loadPayments = async () => {
    try {
      setPayments(await api("/payments"));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const loadPhotos = async () => {
    try {
      setPhotos(await api("/transformations/photos"));
    } catch {}
  };

  const loadGallery = async () => {
    try {
      setGallery(await api("/gallery/all"));
    } catch (err) {
      toast.error(err.message);
    }
  };

  useEffect(() => {
    api("/auth/me")
      .then((u) => {
        setUser(u);
        loadLeads();
        loadArticles();
        loadPayments();
        loadPhotos();
        loadGallery();
      })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const u = await api("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
      setUser(u);
      loadLeads();
      loadArticles();
      loadPayments();
      loadPhotos();
      loadGallery();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    try {
      await api("/auth/logout", { method: "POST" });
    } catch {}
    setUser(null);
    setLeads([]);
    setArticles([]);
    setPayments([]);
  };

  const setStatus = async (id, status) => {
    try {
      const updated = await api(`/leads/${id}/status`, { method: "PATCH", body: JSON.stringify({ status }) });
      setLeads((ls) => ls.map((l) => (l.id === id ? updated : l)));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const exportCSV = () => {
    const blob = new Blob([toCSV(leads)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const saveArticle = async (e) => {
    e.preventDefault();
    setSavingArticle(true);
    try {
      if (editor.id) {
        await api(`/articles/${editor.id}`, { method: "PATCH", body: JSON.stringify(editor) });
        toast.success("Article updated.");
      } else {
        await api("/articles", { method: "POST", body: JSON.stringify(editor) });
        toast.success("Article published.");
      }
      setEditor(null);
      loadArticles();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingArticle(false);
    }
  };

  const deleteArticle = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try {
      await api(`/articles/${a.id}`, { method: "DELETE" });
      toast.success("Article deleted.");
      loadArticles();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const uploadPhoto = async (clientId, file) => {
    setUploading(clientId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media/upload`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(formatApiError(data?.detail));
      await api(`/transformations/${clientId}/photo`, { method: "PUT", body: JSON.stringify({ file_id: data.id }) });
      toast.success("Photo updated — live on the site now.");
      loadPhotos();
    } catch (err) {
      toast.error(err.message || "Upload failed.");
    } finally {
      setUploading(null);
    }
  };

  const removePhoto = async (clientId) => {
    try {
      await api(`/transformations/${clientId}/photo`, { method: "DELETE" });
      toast.success("Photo removed.");
      loadPhotos();
    } catch {}
  };

  const updateGalleryItem = async (id, body) => {
    setGalleryBusy(id);
    try {
      const updated = await api(`/gallery/${id}`, { method: "PATCH", body: JSON.stringify(body) });
      setGallery((g) => g.map((it) => (it.id === id ? { ...it, ...updated } : it)));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setGalleryBusy(null);
    }
  };

  const deleteGalleryItem = async (item) => {
    if (!window.confirm("Remove this image from the gallery permanently?")) return;
    setGalleryBusy(item.id);
    try {
      await api(`/gallery/${item.id}`, { method: "DELETE" });
      setGallery((g) => g.filter((it) => it.id !== item.id));
      toast.success("Image removed.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setGalleryBusy(null);
    }
  };

  const startGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      window.location.origin + "/admin"
    )}`;
  };

  const openPlan = (lead) => {
    setPlanFor(lead);
    setPlanText("");
    api(`/leads/${lead.id}/plan`)
      .then((d) => setPlanText(d.plan))
      .catch(() => {});
  };

  const generatePlan = async (lead) => {
    setPlanLoading(true);
    setPlanText("");
    try {
      const d = await api(`/leads/${lead.id}/generate-plan`, { method: "POST" });
      setPlanText(d.plan);
      toast.success("Plan drafted. Review before sending to the client.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPlanLoading(false);
    }
  };

  const openPlanPdf = async (lead) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads/${lead.id}/plan.pdf`, {
        credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || "Could not build the PDF.");
      }
      const url = URL.createObjectURL(await res.blob());
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const copyPlan = async () => {
    try {
      await navigator.clipboard.writeText(planText);
      toast.success("Plan copied to clipboard.");
    } catch {
      toast.error("Could not copy — select the text manually.");
    }
  };

  const emailPlan = async (lead) => {
    setPlanLoading(true);
    try {
      const d = await api(`/leads/${lead.id}/email-plan`, { method: "POST" });
      toast.success(`Plan emailed to ${d.sent_to}.`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPlanLoading(false);
    }
  };

  const filtered = leads.filter(
    (l) =>
      l.full_name.toLowerCase().includes(search.toLowerCase()) ||
      l.contact.toLowerCase().includes(search.toLowerCase()) ||
      l.target_goal.toLowerCase().includes(search.toLowerCase())
  );

  const paidTotal = payments.filter((p) => p.status === "paid").reduce((s, p) => s + (p.amount || 0), 0);

  if (checking) {
    return (
      <div className="min-h-screen bg-ink text-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-ink text-white">
        <div className="grain" aria-hidden="true" />
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md mx-auto mt-28 rounded-3xl border border-line bg-panel p-10"
          data-testid="admin-login-card"
        >
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gold/10 border border-gold/30">
            <Lock className="h-6 w-6 text-gold" />
          </span>
          <h1 className="mt-6 font-display text-3xl font-extrabold uppercase">Admin Login</h1>
          <p className="mt-2 text-sm text-slate-400">Sign in to manage leads, payments, media and articles.</p>
          <form onSubmit={login} className="mt-8 space-y-5" data-testid="admin-login-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className={inputCls}
              data-testid="admin-login-email-input"
            />
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className={inputCls}
              data-testid="admin-login-pass-input"
            />
            <button
              type="submit"
              disabled={loggingIn}
              data-testid="admin-login-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-accent px-6 py-3.5 font-extrabold uppercase tracking-wide text-ink transition-all hover:shadow-glow-emerald disabled:opacity-60"
            >
              {loggingIn && <Loader2 className="h-4 w-4 animate-spin" />}
              Sign In
            </button>
            <div className="flex items-center gap-3 pt-1">
              <span className="h-px flex-1 bg-line" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">or</span>
              <span className="h-px flex-1 bg-line" />
            </div>
            <button
              type="button"
              onClick={startGoogleLogin}
              data-testid="admin-google-signin-btn"
              className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-white px-6 py-3.5 text-sm font-bold text-slate-800 transition-all hover:bg-slate-100"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
                <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.3-2.1 3.7-5.2 3.7-8.6z" />
                <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-5.9-2.1-6.9-5.1L1.3 17.2C3.3 21.2 7.3 24 12 24z" />
                <path fill="#FBBC05" d="M5.1 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3L1.3 6.8C.5 8.4 0 10.1 0 12s.5 3.6 1.3 5.2l3.8-2.9z" />
                <path fill="#EA4335" d="M12 4.8c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.2 15.2 0 12 0 7.3 0 3.3 2.8 1.3 6.8l3.8 2.9c1-3 3.7-4.9 6.9-4.9z" />
              </svg>
              Sign in with Google
            </button>
            <p className="text-center text-[11px] text-slate-500">
              Google sign-in works only for Nikhil's authorized account.
            </p>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink text-white">
      <div className="grain" aria-hidden="true" />
      <header className="border-b border-line backdrop-blur-xl bg-ink/80 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark className="h-9 w-9" />
            <span className="font-display font-extrabold uppercase text-lg">
              Admin Dashboard
              <span className="block font-mono text-[10px] tracking-[0.3em] text-emerald-400">NIKHIL SHRIRAO</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-sm text-slate-400 hover:text-white transition-colors" data-testid="admin-back-site-link">
              ← Back to site
            </a>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-line text-sm text-slate-300 hover:border-red-500/50 hover:text-red-400 transition-colors"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10">
        <div className="flex flex-wrap gap-2.5 mb-8">
          {[
            { key: "leads", label: "Leads", icon: Users },
            { key: "payments", label: "Payments", icon: Wallet },
            { key: "transformations", label: "Transformations", icon: TrendingDown },
            { key: "media", label: "Client Photos", icon: Images },
            { key: "gallery", label: "Gallery Review", icon: Images },
            { key: "articles", label: "Articles", icon: BookOpen },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-bold uppercase tracking-wide border transition-all duration-300 ${
                tab === t.key
                  ? "bg-accent text-ink border-accent shadow-glow-emerald"
                  : "bg-panel text-slate-300 border-line hover:border-accent/50"
              }`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "transformations" && <TransformationsManager />}

        {tab === "leads" && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, contact, goal..."
                  className={`${inputCls} pl-11`}
                  data-testid="admin-search-input"
                />
              </div>
              <div className="flex items-center gap-4">
                <p className="font-mono text-xs text-slate-500 uppercase tracking-widest" data-testid="admin-lead-count">
                  {filtered.length} lead{filtered.length === 1 ? "" : "s"}
                </p>
                <button
                  onClick={exportCSV}
                  data-testid="admin-export-csv-btn"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-gold/50 text-gold text-sm font-bold uppercase tracking-wide transition-all hover:bg-gold hover:text-ink"
                >
                  <Download className="h-4 w-4" /> Export CSV
                </button>
              </div>
            </div>
            <div className="rounded-3xl border border-line bg-panel overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]" data-testid="admin-leads-table">
                <thead>
                  <tr className="border-b border-line text-left font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                    <th className="px-6 py-4">Date</th>
                    <th className="px-6 py-4">Name</th>
                    <th className="px-6 py-4">Contact</th>
                    <th className="px-6 py-4">Age & Location</th>
                    <th className="px-6 py-4">Weight</th>
                    <th className="px-6 py-4">Obstacle</th>
                    <th className="px-6 py-4">Goal</th>
                    <th className="px-6 py-4">Health</th>
                    <th className="px-6 py-4">AI Plan</th>
                    <th className="px-6 py-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l) => (
                    <tr key={l.id} className="border-b border-line/60 last:border-0 hover:bg-ink/50 transition-colors" data-testid={`admin-lead-row-${l.id}`}>
                      <td className="px-6 py-4 font-mono text-xs text-slate-500">
                        {l.created_at ? new Date(l.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—"}
                      </td>
                      <td className="px-6 py-4 font-semibold text-white">
                        {l.full_name}
                        {l.source === "sample_plan" && (
                          <span
                            className="ml-2 rounded-full border border-blue-400/40 bg-blue-400/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-blue-300"
                            data-testid={`lead-source-badge-${l.id}`}
                          >
                            Sample PDF
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-slate-300">{l.contact}</td>
                      <td className="px-6 py-4 text-slate-300">{l.age_location}</td>
                      <td className="px-6 py-4 font-mono text-emerald-400">{l.current_weight} kg</td>
                      <td className="px-6 py-4 text-slate-400">{l.obstacle}</td>
                      <td className="px-6 py-4 text-slate-300 max-w-[220px]">{l.target_goal}</td>
                      <td className="px-6 py-4 max-w-[220px]" data-testid={`lead-health-${l.id}`}>
                        {l.high_risk && (
                          <span className="mb-1.5 inline-flex items-center gap-1 rounded-full border border-rose-400/40 bg-rose-400/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-rose-300">
                            Screen first
                          </span>
                        )}
                        <p className="text-xs text-slate-400 leading-relaxed">
                          {(l.health_conditions || []).join(", ") || "—"}
                        </p>
                        {l.medications && (
                          <p className="mt-1 font-mono text-[11px] text-slate-500">Rx: {l.medications}</p>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openPlan(l)}
                            data-testid={`lead-ai-plan-btn-${l.id}`}
                            title="Generate AI diet plan draft"
                            className="p-2.5 rounded-full border border-line text-slate-400 transition-colors hover:text-gold hover:border-gold/50"
                          >
                            <Sparkles className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => openPlanPdf(l)}
                            data-testid={`lead-plan-pdf-btn-${l.id}`}
                            title="Open branded plan PDF"
                            className="p-2.5 rounded-full border border-line text-slate-400 transition-colors hover:text-emerald-300 hover:border-accent/50"
                          >
                            <FileDown className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={l.status}
                          onChange={(e) => setStatus(l.id, e.target.value)}
                          data-testid={`admin-lead-status-${l.id}`}
                          className={`rounded-full border px-3 py-1.5 text-xs font-bold uppercase tracking-wide appearance-none focus:outline-none ${
                            l.status === "New"
                              ? "border-gold/50 text-gold bg-gold/10"
                              : l.status === "Contacted"
                              ? "border-blue-400/50 text-blue-300 bg-blue-400/10"
                              : "border-accent/50 text-emerald-300 bg-accent/10"
                          }`}
                        >
                          {STATUSES.map((s) => (
                            <option key={s} value={s} className="bg-ink text-white">
                              {s}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan="10" className="px-6 py-16 text-center text-slate-500 font-mono text-sm" data-testid="admin-empty-state">
                        No applications yet — new leads appear here instantly.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "payments" && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
              <div className="flex flex-wrap gap-3">
                <span className="px-5 py-2.5 rounded-2xl border border-line bg-panel font-mono text-xs uppercase tracking-widest text-slate-300" data-testid="admin-payments-count">
                  {payments.length} transaction{payments.length === 1 ? "" : "s"}
                </span>
                <span className="px-5 py-2.5 rounded-2xl border border-accent/40 bg-accent/10 font-mono text-xs uppercase tracking-widest text-emerald-300" data-testid="admin-payments-total">
                  Collected: {rupees(paidTotal)}
                </span>
              </div>
            </div>
            <div className="rounded-3xl border border-line bg-panel overflow-x-auto">
              <table className="w-full text-sm min-w-[800px]" data-testid="admin-payments-table">
                <thead>
                  <tr className="border-b border-line text-left font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                    <th className="px-6 py-4">Date</th>
                    <th className="px-6 py-4">Product</th>
                    <th className="px-6 py-4">Amount</th>
                    <th className="px-6 py-4">Payment ID</th>
                    <th className="px-6 py-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.order_id} className="border-b border-line/60 last:border-0 hover:bg-ink/50 transition-colors" data-testid={`admin-payment-row-${p.order_id}`}>
                      <td className="px-6 py-4 font-mono text-xs text-slate-500">
                        {p.created_at ? new Date(p.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                      </td>
                      <td className="px-6 py-4 font-semibold text-white">{PRODUCTS[p.product]?.label || p.product}</td>
                      <td className="px-6 py-4 font-mono text-gold">{rupees(p.amount)}</td>
                      <td className="px-6 py-4 font-mono text-xs text-slate-400">{p.payment_id || "—"}</td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-3 py-1 rounded-full font-mono text-[10px] font-bold uppercase tracking-widest ${
                            p.status === "paid"
                              ? "bg-accent/10 border border-accent/40 text-emerald-300"
                              : p.status === "created"
                              ? "bg-gold/10 border border-gold/40 text-gold"
                              : "bg-red-500/10 border border-red-500/40 text-red-300"
                          }`}
                        >
                          {p.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {payments.length === 0 && (
                    <tr>
                      <td colSpan="5" className="px-6 py-16 text-center text-slate-500 font-mono text-sm" data-testid="admin-payments-empty">
                        No transactions yet — enrollments and consultation bookings appear here.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "media" && (
          <div>
            <p className="font-mono text-xs text-slate-500 uppercase tracking-widest mb-8" data-testid="admin-media-hint">
              Upload a before/after photo for each client — it fills the 4:5 slot on the transformations section. JPG, PNG or WebP up to 5 MB.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {CLIENTS.map((c) => {
                const photo = photos[c.id];
                return (
                  <div key={c.id} className="rounded-3xl border border-line bg-panel p-6" data-testid={`admin-media-card-${c.id}`}>
                    <div className="relative aspect-[4/5] w-full overflow-hidden rounded-2xl border border-line bg-gradient-to-br from-ink via-panel to-ink flex flex-col items-center justify-center gap-3">
                      {photo ? (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${photo.url}`}
                          alt={c.name}
                          className="absolute inset-0 h-full w-full object-cover"
                          data-testid={`admin-media-thumb-${c.id}`}
                        />
                      ) : (
                        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent/10 border border-accent/30">
                          <User className="h-7 w-7 text-emerald-300" />
                        </span>
                      )}
                    </div>
                    <p className="mt-4 font-display text-lg font-bold uppercase text-white">{c.name}</p>
                    <div className="mt-4 flex items-center gap-2">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        ref={(el) => (fileInputs.current[c.id] = el)}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) uploadPhoto(c.id, f);
                          e.target.value = "";
                        }}
                        data-testid={`admin-media-input-${c.id}`}
                      />
                      <button
                        type="button"
                        onClick={() => fileInputs.current[c.id]?.click()}
                        disabled={uploading === c.id}
                        data-testid={`admin-media-upload-btn-${c.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-accent text-ink text-xs font-extrabold uppercase tracking-wide transition-all hover:shadow-glow-emerald disabled:opacity-60"
                      >
                        {uploading === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
                        {photo ? "Replace" : "Upload Photo"}
                      </button>
                      {photo && (
                        <button
                          type="button"
                          onClick={() => removePhoto(c.id)}
                          data-testid={`admin-media-remove-btn-${c.id}`}
                          className="p-2.5 rounded-full border border-line text-slate-400 hover:text-red-400 hover:border-red-500/50 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === "gallery" && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
              <div className="flex flex-wrap gap-3">
                <span className="px-5 py-2.5 rounded-2xl border border-accent/40 bg-accent/10 font-mono text-xs uppercase tracking-widest text-emerald-300" data-testid="admin-gallery-visible-count">
                  Published: {gallery.filter((g) => g.visible).length}
                </span>
                <span className="px-5 py-2.5 rounded-2xl border border-gold/40 bg-gold/10 font-mono text-xs uppercase tracking-widest text-gold" data-testid="admin-gallery-hidden-count">
                  Hidden: {gallery.filter((g) => !g.visible).length}
                </span>
              </div>
              <p className="font-mono text-[11px] text-slate-500 uppercase tracking-widest max-w-md" data-testid="admin-gallery-hint">
                Imported from Google Drive — everything starts hidden. Mark items "Published" to show them on the Gallery page.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {gallery.slice(0, galleryChunk).map((g) => (
                <div key={g.id} className="rounded-2xl border border-line bg-panel p-4" data-testid={`admin-gallery-item-${g.id}`}>
                  <div className="relative aspect-square w-full overflow-hidden rounded-xl border border-line bg-ink">
                    {g.kind === "video" ? (
                      <video
                        src={`${process.env.REACT_APP_BACKEND_URL}${g.url}`}
                        preload="metadata"
                        muted
                        playsInline
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${g.url}`}
                        alt={g.original_name}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    )}
                    {!g.visible && (
                      <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-ink/90 border border-gold/40 font-mono text-[9px] font-bold uppercase tracking-widest text-gold">
                        Hidden
                      </span>
                    )}
                  </div>
                  <p className="mt-3 font-mono text-[10px] text-slate-500 truncate" title={g.original_name}>
                    {g.original_name}
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <select
                      value={g.kind}
                      onChange={(e) => updateGalleryItem(g.id, { kind: e.target.value })}
                      data-testid={`admin-gallery-kind-${g.id}`}
                      className="flex-1 rounded-full border border-line bg-ink px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-300 appearance-none focus:outline-none"
                    >
                      <option value="photo" className="bg-ink">Photo</option>
                      <option value="chat" className="bg-ink">Chat</option>
                      <option value="video" className="bg-ink">Video</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => updateGalleryItem(g.id, { visible: !g.visible })}
                      disabled={galleryBusy === g.id}
                      data-testid={`admin-gallery-visible-toggle-${g.id}`}
                      className={`px-4 py-2 rounded-full text-[10px] font-extrabold uppercase tracking-widest border transition-all disabled:opacity-60 ${
                        g.visible
                          ? "bg-accent/10 border-accent/50 text-emerald-300 hover:bg-accent hover:text-ink"
                          : "bg-gold/10 border-gold/50 text-gold hover:bg-gold hover:text-ink"
                      }`}
                    >
                      {galleryBusy === g.id ? "..." : g.visible ? "Published" : "Publish"}
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteGalleryItem(g)}
                      disabled={galleryBusy === g.id}
                      data-testid={`admin-gallery-delete-${g.id}`}
                      className="p-2 rounded-full border border-line text-slate-400 hover:text-red-400 hover:border-red-500/50 transition-colors disabled:opacity-60"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
              {gallery.length === 0 && (
                <div className="col-span-full rounded-3xl border border-dashed border-line bg-panel p-16 text-center" data-testid="admin-gallery-empty">
                  <Images className="h-10 w-10 text-slate-600 mx-auto" />
                  <p className="mt-5 font-display text-xl font-bold uppercase text-white">Gallery is empty</p>
                  <p className="mt-2 text-sm text-slate-400">Imported Drive images will appear here for review.</p>
                </div>
              )}
            </div>
            {gallery.length > galleryChunk && (
              <div className="mt-8 text-center">
                <button
                  type="button"
                  onClick={() => setGalleryChunk((c) => c + 24)}
                  data-testid="admin-gallery-load-more"
                  className="px-7 py-3 rounded-full border border-line text-slate-300 text-sm font-bold uppercase tracking-wide transition-all hover:border-accent/50 hover:text-emerald-300"
                >
                  Load More ({gallery.length - galleryChunk} remaining)
                </button>
              </div>
            )}
          </div>
        )}

        {tab === "articles" && (
          <div>
            {editor ? (
              <motion.form
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                onSubmit={saveArticle}
                className="rounded-3xl border border-line bg-panel p-8 space-y-5 max-w-3xl"
                data-testid="admin-article-form"
              >
                <div className="flex items-center justify-between">
                  <h2 className="font-display text-2xl font-extrabold uppercase text-white">
                    {editor.id ? "Edit Article" : "New Article"}
                  </h2>
                  <button
                    type="button"
                    onClick={() => setEditor(null)}
                    data-testid="admin-article-cancel-btn"
                    className="p-2 rounded-full border border-line text-slate-400 hover:text-white transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <input
                  required
                  value={editor.title}
                  onChange={(e) => setEditor({ ...editor, title: e.target.value })}
                  placeholder="Article title"
                  className={inputCls}
                  data-testid="admin-article-title-input"
                />
                <div className="grid sm:grid-cols-2 gap-5">
                  <select
                    value={editor.category}
                    onChange={(e) => setEditor({ ...editor, category: e.target.value })}
                    className={`${inputCls} appearance-none`}
                    data-testid="admin-article-category-select"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c} className="bg-ink">
                        {c}
                      </option>
                    ))}
                  </select>
                  <label className="flex items-center gap-3 px-4 py-3 rounded-xl bg-ink border border-line cursor-pointer" data-testid="admin-article-published-toggle">
                    <input
                      type="checkbox"
                      checked={editor.published}
                      onChange={(e) => setEditor({ ...editor, published: e.target.checked })}
                      className="h-4 w-4 accent-emerald-500"
                    />
                    <span className="text-sm font-semibold text-slate-300">Published (visible on site)</span>
                  </label>
                </div>
                <textarea
                  required
                  rows={2}
                  value={editor.excerpt}
                  onChange={(e) => setEditor({ ...editor, excerpt: e.target.value })}
                  placeholder="Short excerpt shown on cards (1-2 sentences)"
                  className={inputCls}
                  data-testid="admin-article-excerpt-input"
                />
                <textarea
                  required
                  rows={12}
                  value={editor.content}
                  onChange={(e) => setEditor({ ...editor, content: e.target.value })}
                  placeholder={"Full article body. Blank line between paragraphs. Use '## Heading' for section headings."}
                  className={`${inputCls} font-mono text-sm leading-relaxed`}
                  data-testid="admin-article-content-input"
                />
                <button
                  type="submit"
                  disabled={savingArticle}
                  data-testid="admin-article-save-btn"
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-accent text-ink font-extrabold uppercase tracking-wide text-sm transition-all hover:shadow-glow-emerald disabled:opacity-60"
                >
                  {savingArticle ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save Article
                </button>
              </motion.form>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-8">
                  <p className="font-mono text-xs text-slate-500 uppercase tracking-widest" data-testid="admin-article-count">
                    {articles.length} article{articles.length === 1 ? "" : "s"}
                  </p>
                  <button
                    onClick={() => setEditor({ ...EMPTY_ARTICLE })}
                    data-testid="admin-article-new-btn"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold text-ink text-sm font-bold uppercase tracking-wide transition-all hover:shadow-glow-gold"
                  >
                    <Plus className="h-4 w-4" /> New Article
                  </button>
                </div>
                <div className="space-y-3">
                  {articles.map((a) => (
                    <div
                      key={a.id}
                      className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-2xl border border-line bg-panel p-6"
                      data-testid={`admin-article-row-${a.id}`}
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="px-2.5 py-0.5 rounded-full bg-gold/10 border border-gold/30 font-mono text-[10px] font-bold uppercase tracking-widest text-gold">
                            {a.category}
                          </span>
                          <span
                            className={`px-2.5 py-0.5 rounded-full font-mono text-[10px] font-bold uppercase tracking-widest ${
                              a.published ? "bg-accent/10 border border-accent/40 text-emerald-300" : "bg-ink border border-line text-slate-500"
                            }`}
                          >
                            {a.published ? "Live" : "Draft"}
                          </span>
                        </div>
                        <p className="mt-2 font-display text-lg font-bold uppercase text-white truncate">{a.title}</p>
                        <p className="text-xs text-slate-500 font-mono">/blog/{a.slug}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <a
                          href={`/blog/${a.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`admin-article-view-${a.id}`}
                          className="px-4 py-2 rounded-full border border-line text-xs font-bold uppercase tracking-wide text-slate-300 hover:border-accent/50 hover:text-emerald-300 transition-colors"
                        >
                          View
                        </a>
                        <button
                          onClick={() => setEditor({ ...a })}
                          data-testid={`admin-article-edit-${a.id}`}
                          className="p-2.5 rounded-full border border-line text-slate-400 hover:text-gold hover:border-gold/50 transition-colors"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteArticle(a)}
                          data-testid={`admin-article-delete-${a.id}`}
                          className="p-2.5 rounded-full border border-line text-slate-400 hover:text-red-400 hover:border-red-500/50 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {articles.length === 0 && (
                    <div className="rounded-3xl border border-dashed border-line bg-panel p-16 text-center" data-testid="admin-articles-empty">
                      <BookOpen className="h-10 w-10 text-slate-600 mx-auto" />
                      <p className="mt-5 font-display text-xl font-bold uppercase text-white">No articles yet</p>
                      <p className="mt-2 text-sm text-slate-400">Write your first evidence-based guide for the journal.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {planFor && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-ink/90 p-4 backdrop-blur-sm"
          onClick={() => setPlanFor(null)}
          data-testid="ai-plan-modal"
        >
          <div
            className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl border border-line bg-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 border-b border-line p-7">
              <div className="min-w-0">
                <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                  <Sparkles className="h-3.5 w-3.5" /> Gemini AI Draft
                </p>
                <h3 className="mt-2 truncate font-display text-2xl font-extrabold uppercase text-white">
                  {planFor.full_name}
                </h3>
                <p className="mt-1 text-xs text-slate-500 font-mono">
                  {planFor.current_weight} kg · {planFor.obstacle} · Goal: {planFor.target_goal}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPlanFor(null)}
                data-testid="ai-plan-close-btn"
                className="p-2 rounded-full border border-line text-slate-400 hover:text-white transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-7" data-testid="ai-plan-body">
              {planLoading ? (
                <div className="flex flex-col items-center justify-center gap-4 py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-400">
                    Gemini is drafting the plan...
                  </p>
                </div>
              ) : planText ? (
                <div className="whitespace-pre-wrap font-mono text-[13px] leading-relaxed text-slate-200" data-testid="ai-plan-text">
                  {planText}
                </div>
              ) : (
                <div className="py-12 text-center" data-testid="ai-plan-empty">
                  <Sparkles className="h-9 w-9 mx-auto text-slate-600" />
                  <p className="mt-4 font-display text-lg font-bold uppercase text-white">No plan yet</p>
                  <p className="mx-auto mt-2 max-w-sm text-sm text-slate-400">
                    Draft a holistic, food-first plan from this lead's details and health flags, then review and
                    adjust it before sending.
                  </p>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-line p-6">
              <button
                type="button"
                onClick={() => generatePlan(planFor)}
                disabled={planLoading}
                data-testid="ai-plan-generate-btn"
                className="inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3 text-xs font-extrabold uppercase tracking-wide text-ink transition-all hover:shadow-glow-emerald disabled:opacity-60"
              >
                {planLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {planText ? "Regenerate" : "Generate Plan"}
              </button>
              {planText && !planLoading && (
                <div className="flex items-center gap-3">
                  {planFor.contact.includes("@") && (
                    <button
                      type="button"
                      onClick={() => emailPlan(planFor)}
                      disabled={planLoading}
                      data-testid="ai-plan-email-btn"
                      className="inline-flex items-center gap-2 rounded-full border border-accent/50 px-5 py-3 text-xs font-bold uppercase tracking-wide text-emerald-300 transition-all hover:bg-accent hover:text-ink disabled:opacity-60"
                    >
                      {planLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                      Email To Lead
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => openPlanPdf(planFor)}
                    data-testid="ai-plan-pdf-btn"
                    className="inline-flex items-center gap-2 rounded-full border border-line px-5 py-3 text-xs font-bold uppercase tracking-wide text-slate-300 transition-all hover:border-slate-500 hover:text-white"
                  >
                    <FileDown className="h-3.5 w-3.5" /> PDF
                  </button>
                  <button
                    type="button"
                    onClick={copyPlan}
                    data-testid="ai-plan-copy-btn"
                    className="inline-flex items-center gap-2 rounded-full border border-gold/50 px-5 py-3 text-xs font-bold uppercase tracking-wide text-gold transition-all hover:bg-gold hover:text-ink"
                  >
                    <Copy className="h-3.5 w-3.5" /> Copy
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}